import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Plus, Printer, X } from 'lucide-react';
import BackArrow from '../components/ui/BackArrow';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import LoadingState from '../components/LoadingState';
import Button from '../components/ui/Button';
import SegmentedControl from '../components/ui/SegmentedControl';
import { ErrorState } from '../components/ui/States';
import {
  CreditNote,
  CreditNoteStockReturnRecord,
  CreditNoteType,
  CreditableLine,
  CreditableLines,
  GoodsDestination,
  ReturnSources,
  StockReturnEntry,
  SupplierReturnOffer,
  cancelCreditNote,
  createCreditNote,
  getCreditNote,
  getCreditableLines,
  getNextCreditNoteNumber,
  getReturnSources,
  getSupplierReturnOffer,
  hasRaisableSupplierReturn,
  isCreditNoteEditable,
  issueCreditNote,
  fetchCreditNotePdf,
  previewNetValue,
  raisedReturnsOf,
  updateCreditNote,
} from '../lib/creditNoteApi';
import { openPrintInNewTab } from '../lib/serverPrint';
import { CreditReason, listCreditReasons } from '../lib/creditReasonApi';
import { ClientInvoiceDto, InvoicePayer, fetchClientInvoiceById, payerOf } from '../lib/clientInvoiceApi';
import InvoiceSearchSelect from '../components/InvoiceSearchSelect';
import { useConfirm } from '../components/ui/ConfirmProvider';
import CreditReasonField from '../features/creditNotes/CreditReasonField';
import GoodsDestinationField from '../features/creditNotes/GoodsDestinationField';
import StockReturnSplit from '../features/creditNotes/StockReturnSplit';
import StockReturnSummary, { StockReturnShortfallNotice } from '../features/creditNotes/StockReturnSummary';
import SupplierReturnDialog from '../features/creditNotes/SupplierReturnDialog';
import {
  RowSplit,
  noSourcesMessage,
  sourceLineOf,
  splitFromSaved,
  stockReturnPayloadOf,
} from '../features/creditNotes/stockReturnDraft';
import { errorMessageOf, refusedLineNumbers } from '../features/debitNotes/debitNoteDisplay';
import { currencyPrefix, formatCurrency } from '../utils/priceUtils';
import DateInput from "../components/DateInput";
import { CATEGORY_SELECT_OPTIONS } from '../utils/invoiceCategories';

type ItemRow = {
  lineNumber?: number;
  quantity: number;
  uom: string;
  description: string;
  unitPrice: number;
  /** Which line of the source invoice this row credits. Set when the row was added
   *  from the invoice; null for a free row. The server caps a row that has one. */
  sourceInvoiceLineNumber?: number | null;
  /** D44 #8 — Item Return into United stock, on a draft: how the goods split across batches. */
  split?: RowSplit;
  /** As the server stored it: what Issue put back and Cancel took out. Read-only. */
  stockReturn?: StockReturnEntry[];
};

/** D96.17 — `currency` is the invoice's; the note's is the first ref's. */
type LinkedInvoiceRef = { id: string; number: string; currency: string };

type FormState = {
  _id?: string;
  creditNoteNumber: string;
  type: CreditNoteType | '';
  category: string;
  vesselName: string;
  ownerName: string;
  ownerAddress: string;
  agentName: string;
  /** D153.6 — who pays, copied from the invoice; the print marks PAYER and prints the payer's address. */
  payer: InvoicePayer;
  agentAddress: string;
  clientName: string;
  poNumber: string;
  /** D96.17 — the currency PICKED for a note with no invoice; '' = none. Read the note's via `noteCurrencyOf`. */
  currency: string;
  /** D61 (USC-BUG-0027) — the day the clerk chose, yyyy-mm-dd; '' = none chosen. */
  creditNoteDate: string;
  /** What the server would date the note if none is chosen (`issuedAt || createdAt`). Read-only. */
  dateFallback: string;
  status: string;
  notes: string;
  items: ItemRow[];
  linkedInvoiceRefs: LinkedInvoiceRef[];
  discountPercent: number;
  /** D44 — why the credit is given. Internal: never printed on the client's copy. */
  reasonKey: string;
  reasonNote: string;
  /** The reason as last saved, so a reason an admin has since hidden stays selectable. */
  savedReasonKey: string;
  /** D44 #7 — Item Return only: where the goods went. '' until answered. */
  goodsDestination: GoodsDestination | '';
  /** D44 #8, #39 — what Issue and Cancel did to stock, as stored. Read-only. */
  stockReturn: CreditNoteStockReturnRecord | null;
};

const PAGE_INFO = (
  <div className="flex flex-col gap-3">
    <p>Create or edit a credit note against a client invoice. Every value on a credit note is negative.</p>
    <p className="font-medium">Types (choose exactly one):</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li>Item Return — negative line items for goods sent back</li>
      <li>Discount — a % off one or more linked invoices, netted against prior credits</li>
      <li>Correction — negative line items reversing a billing mistake</li>
    </ul>
    <p>The type cannot be changed once the note is saved. A note is in its invoice&apos;s currency; one with no invoice needs a currency picked.</p>
    <p>
      Every credit note needs a reason. It stays internal and is not printed on the client&apos;s copy.
    </p>
    <p>
      A note is a Draft until you Issue it. Issuing locks it; to change an issued note, cancel it and
      raise a new one.
    </p>
    <p>
      An Item Return says where the goods went: back into United stock, straight to the vendor, or not
      returned. Goods back into United stock go into the batches they were shipped from when the note is
      issued, and come back out, as far as they are still free, if it is cancelled. After issuing, vendor
      returns can be raised from the note as drafts.
    </p>
  </div>
);


const TYPE_OPTIONS: Array<{ value: CreditNoteType; label: string }> = [
  { value: 'item_return', label: 'Item Return' },
  { value: 'discount', label: 'Discount' },
  { value: 'correction', label: 'Correction' },
];

/**
 * D96.17 — the note's currency, worked out, never stored: a linked note is in its first
 * invoice's (the server refuses a mixed set, so every linked invoice agrees); a note with
 * none is in the one picked. Stored copies went stale on unlink and type change.
 */
function noteCurrencyOf(form: FormState): string {
  return form.linkedInvoiceRefs.length ? form.linkedInvoiceRefs[0].currency : form.currency;
}

/** D96.17 — in the note's own currency (the server print names it too); none stated, no symbol. */
function moneyIn(value: number | null | undefined, currency: string) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '--';
  return formatCurrency(value, currency || null, { ceil: false });
}

const CURRENCY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Select currency' },
  { value: 'USD', label: 'US$' },
  { value: 'TTD', label: 'TT$' },
];

function formatStatusLabel(status: string) {
  const s = String(status || '').toLowerCase();
  if (s === 'draft') return 'Draft';
  if (s === 'issued') return 'Issued';
  if (s === 'applied') return 'Applied';
  if (s === 'cancelled') return 'Cancelled';
  return String(status || '--');
}

function round2(n: number) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** Mirrors the backend's recomputeItemTotals: qty forced negative, unitPrice forced positive. */
function computeExtension(quantity: number, unitPrice: number) {
  const q = Number(quantity) || 0;
  const p = Math.abs(Number(unitPrice) || 0);
  const negQty = q > 0 ? -q : q;
  return round2(negQty * p);
}

function formatEditableNumber(value: unknown) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n.toFixed(2);
}

function blankItemRow(type: CreditNoteType | ''): ItemRow {
  const hint = type === 'correction' ? 'Reversal of item ' : type === 'item_return' ? 'Item return ' : '';
  return { quantity: 0, uom: '', description: hint, unitPrice: 0 };
}

/** The untouched placeholder row the form starts with — dropped the moment a real
 *  invoice line is added, so the note does not save a $0 ghost line beside it. */
function isBlankRow(row: ItemRow) {
  return (
    !row.sourceInvoiceLineNumber &&
    (Number(row.quantity) || 0) === 0 &&
    (Number(row.unitPrice) || 0) === 0
  );
}

// Today as <input type="date"> spells it, in the BROWSER's zone — en-CA formats
// yyyy-mm-dd. Not toISOString(), which is the UTC day and so is tomorrow from
// 20:00 Trinidad onward. Courtesy only: the server owns the refusal
// (CREDIT_NOTE_DATE_IN_FUTURE, against the Trinidad day).
function todayInputValue(): string {
  return new Date().toLocaleDateString('en-CA');
}

function emptyForm(): FormState {
  return {
    creditNoteNumber: '',
    type: '',
    category: '',
    vesselName: '',
    ownerName: '',
    ownerAddress: '',
    agentName: '',
    payer: 'agent',
    agentAddress: '',
    clientName: '',
    poNumber: '',
    currency: '',
    // D61 — left unchosen on a new note, so an untouched one still falls back to
    // the issue timestamp. It used to be prefilled from toISOString(), which is
    // the UTC day: past 20:00 Trinidad that is tomorrow, and tomorrow is refused.
    creditNoteDate: '',
    dateFallback: '',
    status: 'draft',
    notes: '',
    items: [],
    linkedInvoiceRefs: [],
    discountPercent: 0,
    reasonKey: '',
    reasonNote: '',
    savedReasonKey: '',
    goodsDestination: '',
    stockReturn: null,
  };
}

/** A stored note as the form holds it. */
function formFromNote(cn: CreditNote): FormState {
  return {
    _id: cn._id,
    creditNoteNumber: cn.creditNoteNumber || '',
    type: cn.type,
    category: cn.category || '',
    vesselName: cn.vesselName || '',
    ownerName: cn.ownerName || '',
    ownerAddress: cn.ownerAddress || '',
    agentName: cn.agentName || '',
    payer: payerOf(cn),
    agentAddress: cn.agentAddress || '',
    clientName: cn.clientName || '',
    poNumber: cn.poNumber || '',
    // A linked note's currency lives on its refs; only a note with no invoice keeps a pick.
    currency: (cn.linkedInvoices || []).length ? '' : cn.currency || '',
    creditNoteDate: String(cn.creditNoteDate || '').slice(0, 10),
    dateFallback: String(cn.issuedAt || cn.createdAt || '').slice(0, 10),
    status: cn.status || 'draft',
    notes: cn.notes || '',
    items: (cn.items || []).map((it) => ({
      lineNumber: it.lineNumber,
      quantity: Number(it.quantity) || 0,
      uom: it.uom || '',
      description: it.description || '',
      unitPrice: Number(it.unitPrice) || 0,
      // Kept, or re-saving an edited note sent every row as a free row: the server
      // then skipped the per-line caps and the picker showed the line as uncredited.
      sourceInvoiceLineNumber: it.sourceInvoiceLineNumber ?? null,
      split: splitFromSaved(it.stockReturn, Number(it.quantity) || 0),
      stockReturn: it.stockReturn || [],
    })),
    linkedInvoiceRefs: (cn.linkedInvoices || []).map((invId, idx) => ({
      id: String(invId),
      number: (cn.linkedInvoiceNumbers || [])[idx] || '',
      // A draft re-saves in its invoice's currency as it is now, so it shows that (a
      // pre-D96 note may hold a guessed 'USD'); an issued/cancelled note is what it is.
      currency: (cn.status || 'draft') === 'draft' ? (cn.linkedInvoiceCurrencies || [])[idx] || '' : cn.currency || '',
    })),
    discountPercent: cn.discountPercent || 0,
    reasonKey: cn.reasonKey || '',
    reasonNote: cn.reasonNote || '',
    savedReasonKey: cn.reasonKey || '',
    goodsDestination: cn.goodsDestination || '',
    stockReturn: cn.stockReturn || null,
  };
}

export const CreditNoteDetailPage: React.FC = () => {
  const params = useParams();
  const id = params.id;
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateValid, setDateValid] = useState(true);
  // USC-BUG-0028 — the refusal renders at the top of the scrolling pane; a user
  // who pressed Save with the Category field in view never saw it, so the note
  // looked saved until a reload. Bring it into view whenever it changes.
  // Round 2: scrollIntoView (smooth, 'nearest') left the viewport unmoved on QA
  // — the pane sits inside MainLayout's own overflow-auto <main>. So scroll the
  // pane AND every scrolling ancestor to the top, instantly: the banner is the
  // pane's first child, directly under the header, whichever container scrolled.
  const paneRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!error) return;
    for (let el: HTMLElement | null = paneRef.current; el; el = el.parentElement) {
      if (el.scrollTop) el.scrollTop = 0;
    }
  }, [error]);
  const [form, setForm] = useState<FormState>(emptyForm());
  const money = (value: number | null | undefined) => moneyIn(value, noteCurrencyOf(form));
  const cur = currencyPrefix(noteCurrencyOf(form)).trim();
  // D44 — loaded WITH hidden reasons, so a note filed under one still shows it.
  const [reasons, setReasons] = useState<CreditReason[]>([]);
  const locked = !isNew && !isCreditNoteEditable(form.status);
  // A message that must survive the reload a navigation triggers (see handleIssue).
  const carriedError = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listCreditReasons({ includeHidden: true })
      .then((list) => {
        if (!cancelled) setReasons(list);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.message || 'Could not load the reason list');
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const [netInvoiceValue, setNetInvoiceValue] = useState(0);
  // Rendered by the server so the on-screen preview is exactly what prints on the note.
  const [discountDescription, setDiscountDescription] = useState('');
  // USC-BUG-0028 — why the preview is blank. A refused preview used to be swallowed
  // and shown as $0.00, which reads as "this discount is worth nothing" rather than
  // "the Category matches none of these invoices". It renders beside the fields.
  const [previewError, setPreviewError] = useState<string | null>(null);

  // The source invoice's lines and what is left to credit on each. Null until an
  // invoice is linked — with no invoice there is nothing to cap against and the
  // grid stays a free-text one, exactly as it was.
  const [creditable, setCreditable] = useState<CreditableLines | null>(null);
  const [creditableLoading, setCreditableLoading] = useState(false);

  // D44 #8 (VR-4) — Item Return "Back into United stock": the batches each invoice
  // line shipped from. Null until read.
  const [returnSources, setReturnSources] = useState<ReturnSources | null>(null);
  const [returnSourcesLoading, setReturnSourcesLoading] = useState(false);
  const [returnSourcesError, setReturnSourcesError] = useState<string | null>(null);
  /** Invoice line numbers the last refusal named: marked on the rows and the split. */
  const [flaggedLines, setFlaggedLines] = useState<number[]>([]);
  const carriedFlags = useRef<number[] | null>(null);

  // D44 #9 (VR-4) — "Return to supplier?" on an issued Item Return.
  const [supplierOffer, setSupplierOffer] = useState<SupplierReturnOffer | null>(null);
  const [supplierOfferError, setSupplierOfferError] = useState<string | null>(null);
  const [supplierOfferReload, setSupplierOfferReload] = useState(0);
  /** The offer the open dialog was opened with; null while it is closed. */
  const [supplierDialogOffer, setSupplierDialogOffer] = useState<SupplierReturnOffer | null>(null);
  /** Set by a successful Issue: open the dialog once the offer is read, if anything can be raised. */
  const offerAfterIssue = useRef(false);

  const inputClass = 'w-full h-8 rounded-md border border-slate-200 px-2 text-xs focus:border-primary-500';
  const labelClass = 'block text-xs font-medium text-slate-600 mb-1';

  // Load existing note, or seed a blank one with the next CR number for a new note.
  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        if (isNew) {
          const nextNumber = await getNextCreditNoteNumber();
          if (cancelled) return;
          setForm({ ...emptyForm(), creditNoteNumber: nextNumber });
          setNetInvoiceValue(0);
          setDiscountDescription('');
          // Item 15 (9 Oct 2026 QA retest): "Raise credit note" from an invoice carries
          // ?clientInvoiceId=; pre-fill exactly as picking that invoice by hand would
          // (pickInvoice, below) rather than a second copy of the same field mapping.
          // A failed fetch surfaces through the same catch as a bad CR-number read
          // below, rather than being swallowed: a stale or deleted link should say so,
          // not open a blank form that looks like it worked.
          const prefillInvoiceId = searchParams.get('clientInvoiceId');
          if (prefillInvoiceId) {
            const inv = await fetchClientInvoiceById(prefillInvoiceId);
            if (cancelled) return;
            if (inv) pickInvoice(inv);
          }
        } else if (id) {
          const cn = await getCreditNote(id);
          if (cancelled) return;
          setForm(formFromNote(cn));
          setNetInvoiceValue(cn.netInvoiceValue || 0);
          setDiscountDescription((cn.items || [])[0]?.description || '');
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load credit note');
      } finally {
        if (!cancelled) {
          setLoading(false);
          if (carriedError.current) {
            setError(carriedError.current);
            carriedError.current = null;
          }
          if (carriedFlags.current) {
            setFlaggedLines(carriedFlags.current);
            carriedFlags.current = null;
          }
        }
      }
    }
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Live net-value + description preview (discount only). The server nets off any prior
  // credit notes against the linked invoices — the percentage applies to that net, never
  // to the original invoice value.
  useEffect(() => {
    if (form.type !== 'discount') return;
    const ids = form.linkedInvoiceRefs.map((r) => r.id).filter(Boolean);
    if (!ids.length) {
      setNetInvoiceValue(0);
      setDiscountDescription('');
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      try {
        // USC-BUG-0028 / D58 — the Category goes with it, and is a dependency below:
        // Save scopes the discount by category, so a preview without it is computed
        // by a different rule than the Save it is previewing.
        const bd = await previewNetValue(ids, Number(form.discountPercent) || 0, form.category);
        if (cancelled) return;
        setNetInvoiceValue(Number(bd.netInvoiceValue) || 0);
        setDiscountDescription(bd.description || '');
        setPreviewError(null);
      } catch (e: unknown) {
        if (!cancelled) {
          setNetInvoiceValue(0);
          setDiscountDescription('');
          // Never a silent zero: say why there is nothing to discount.
          setPreviewError(errorMessageOf(e, 'The net value of these invoices could not be worked out.'));
        }
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.type, form.linkedInvoiceRefs, form.discountPercent, form.category]);

  const grandTotal = useMemo(() => {
    if (form.type === 'discount') {
      const raw = round2(((Number(form.discountPercent) || 0) / 100) * netInvoiceValue);
      return raw === 0 ? 0 : -Math.abs(raw);
    }
    return round2(form.items.reduce((s, it) => s + computeExtension(it.quantity, it.unitPrice), 0));
  }, [form.type, form.items, form.discountPercent, netInvoiceValue]);

  const patch = (k: keyof FormState, v: any) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleTypeChange = (nextType: CreditNoteType) => {
    if (!isNew) return;
    setForm((prev) => {
      // Item 15: a note's FIRST type pick (prev.type === '') has nothing of its own to
      // reset — the only way linkedInvoiceRefs can be non-empty here is the "Raise
      // credit note" prefill (pickInvoice ran before any type was chosen), and that
      // link is what the clerk opened this form for. Choosing a DIFFERENT type later
      // (prev.type already set) still clears it, same as before.
      const linkedInvoiceRefs = prev.type === '' ? prev.linkedInvoiceRefs : [];
      if (nextType === 'discount') {
        return { ...prev, type: nextType, items: [], linkedInvoiceRefs, discountPercent: 0 };
      }
      return {
        ...prev,
        type: nextType,
        linkedInvoiceRefs,
        discountPercent: 0,
        items: [blankItemRow(nextType)],
        goodsDestination: '',
      };
    });
    setNetInvoiceValue(0);
    setDiscountDescription('');
  };

  // Fetched whenever the linked invoice or the type changes. Saving navigates
  // away, so the allowance is re-read on the way back in rather than refreshed
  // in place.
  const anchorInvoiceId = form.linkedInvoiceRefs[0]?.id || '';
  useEffect(() => {
    const isLineType = form.type === 'item_return' || form.type === 'correction';
    if (!isLineType || !anchorInvoiceId) {
      setCreditable(null);
      return;
    }
    let cancelled = false;
    setCreditableLoading(true);
    (async () => {
      try {
        // Editing an existing note: its own lines hold part of the allowance
        // already, so they must not be counted against it.
        const data = await getCreditableLines(anchorInvoiceId, form._id || undefined);
        if (!cancelled) setCreditable(data);
      } catch {
        if (!cancelled) setCreditable(null);
      } finally {
        if (!cancelled) setCreditableLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [anchorInvoiceId, form.type, form._id]);

  // D44 #8 — where each invoice line's goods can go back, for an Item Return whose
  // goods went back into United stock. A draft is read without its own split
  // counted against it; an issued or cancelled one only needs the names.
  const wantsReturnSources =
    form.type === 'item_return' && form.goodsDestination === 'united_stock' && Boolean(anchorInvoiceId);
  useEffect(() => {
    if (!wantsReturnSources) {
      setReturnSources(null);
      setReturnSourcesError(null);
      setReturnSourcesLoading(false);
      return;
    }
    let cancelled = false;
    setReturnSourcesLoading(true);
    setReturnSourcesError(null);
    getReturnSources(anchorInvoiceId, !locked && form._id ? form._id : undefined)
      .then((data) => {
        if (!cancelled) setReturnSources(data);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setReturnSources(null);
        setReturnSourcesError(errorMessageOf(e, "Where this invoice's goods went could not be read."));
      })
      .finally(() => {
        if (!cancelled) setReturnSourcesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [wantsReturnSources, anchorInvoiceId, form._id, locked]);

  // D44 #9 — what "Return to supplier?" can still raise, on an issued Item Return.
  const offerNoteId =
    form.status === 'issued' && form.type === 'item_return' && form.goodsDestination ? form._id || '' : '';
  useEffect(() => {
    if (!offerNoteId) {
      setSupplierOffer(null);
      setSupplierOfferError(null);
      return;
    }
    let cancelled = false;
    getSupplierReturnOffer(offerNoteId)
      .then((offer) => {
        if (cancelled) return;
        setSupplierOffer(offer);
        setSupplierOfferError(null);
        // Right after Issue the dialog opens by itself, but only when there is
        // something it could raise.
        if (offerAfterIssue.current) {
          offerAfterIssue.current = false;
          if (hasRaisableSupplierReturn(offer)) setSupplierDialogOffer(offer);
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        offerAfterIssue.current = false;
        setSupplierOffer(null);
        setSupplierOfferError(errorMessageOf(e, 'Which vendor returns can be raised from this note could not be read.'));
      });
    return () => {
      cancelled = true;
    };
  }, [offerNoteId, supplierOfferReload]);

  /** Change one row's split across batches. */
  const updateSplit = (index: number, next: RowSplit) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((it, i) => (i === index ? { ...it, split: next } : it)),
    }));
  };

  /** What this form has already put on a given invoice line, so the picker can
   *  count the rows in front of you and not just the ones already saved. */
  const stagedOnLine = (lineNumber: number) =>
    form.items
      .filter((it) => it.sourceInvoiceLineNumber === lineNumber)
      .reduce((sum, it) => sum + Math.abs(Number(it.quantity) || 0), 0);

  const remainingOnLine = (line: CreditableLine) =>
    Math.max(0, round2(line.remainingQuantity - stagedOnLine(line.lineNumber)));

  /** Add an invoice line to the note, priced as the client was billed. */
  const addInvoiceLine = (line: CreditableLine) => {
    const remaining = remainingOnLine(line);
    if (remaining <= 0) return;
    setForm((prev) => ({
      ...prev,
      items: [
        ...prev.items.filter((it) => !isBlankRow(it)),
        {
          quantity: remaining,
          uom: line.uom,
          description: line.itemName || line.itemDescription || `Line ${line.lineNumber}`,
          unitPrice: line.unitPrice ?? 0,
          sourceInvoiceLineNumber: line.lineNumber,
        },
      ],
    }));
  };

  const updateItem = (idx: number, itemPatch: Partial<ItemRow>) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((it, i) => (i === idx ? { ...it, ...itemPatch } : it)),
    }));
  };

  // What is IN the box while it is being typed in, before it is a number.
  //
  // The cell used to render `value.toFixed(2)` on every keystroke, so the box
  // rewrote itself under the cursor: typing 9-0 for ninety gave "9.00" after the
  // first key and "9.000" after the second, and a decimal point was swallowed
  // outright because Number("1.") is 1. The raw text is held here until the field
  // is left, and only then formatted.
  const [rawEdits, setRawEdits] = useState<Record<string, string>>({});
  const [rawPercent, setRawPercent] = useState<string | undefined>(undefined);
  const rawKey = (idx: number, field: 'quantity' | 'unitPrice') => `${idx}:${field}`;

  const displayNumber = (idx: number, field: 'quantity' | 'unitPrice', value: number) => {
    const raw = rawEdits[rawKey(idx, field)];
    return raw !== undefined ? raw : formatEditableNumber(value);
  };

  const commitNumber = (idx: number, field: 'quantity' | 'unitPrice') => {
    setRawEdits((prev) => {
      const next = { ...prev };
      delete next[rawKey(idx, field)];
      return next;
    });
  };

  const updateItemNumber = (idx: number, field: 'quantity' | 'unitPrice', value: string) => {
    const n = value === '' ? 0 : Number(value);
    let next = Number.isFinite(n) ? round2(n) : 0;
    // A row added from the invoice cannot be typed past what that line has left.
    // The server refuses it too — this is so nobody fills a number in, saves, and
    // is only then told no. `max` alone does not stop typing.
    let clamped = false;
    if (field === 'quantity') {
      const cap = capForRow(form.items[idx], idx);
      if (cap !== null && next > cap) {
        next = cap;
        clamped = true;
      }
    }
    // Show the clamp happening rather than letting the box read 500 until blur.
    setRawEdits((prev) => ({ ...prev, [rawKey(idx, field)]: clamped ? String(next) : value }));
    updateItem(idx, { [field]: next } as Partial<ItemRow>);
  };

  /** What this row may credit: the invoice line's remaining, less whatever the
   *  OTHER rows of this note have already staged against the same line. */
  const capForRow = (row: ItemRow | undefined, idx: number): number | null => {
    if (!row?.sourceInvoiceLineNumber || !creditable) return null;
    const line = creditable.lines.find((l) => l.lineNumber === row.sourceInvoiceLineNumber);
    if (!line) return null;
    const stagedElsewhere = form.items.reduce(
      (sum, it, i) =>
        i !== idx && it.sourceInvoiceLineNumber === row.sourceInvoiceLineNumber
          ? sum + Math.abs(Number(it.quantity) || 0)
          : sum,
      0
    );
    return Math.max(0, round2(line.remainingQuantity - stagedElsewhere));
  };

  const addRow = () => {
    setForm((prev) => ({ ...prev, items: [...prev.items, blankItemRow(prev.type)] }));
  };

  const removeItem = (idx: number) => {
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  // Selecting a source invoice auto-fills the header from it (still editable) — the credit
  // note "relates to" that invoice, so its vessel/owner/agent/PO#/category carry over
  // (Matthew / Registers R7). A discount may link several invoices (net calc), anchoring the
  // header to the first; Item Return / Correction use one source invoice.
  const pickInvoice = (inv: ClientInvoiceDto) => {
    setForm((prev) => {
      const ref = { id: inv._id, number: inv.invoiceNumber, currency: String(inv.currency || '').toUpperCase() };
      const refs =
        prev.type === 'discount'
          ? prev.linkedInvoiceRefs.some((r) => r.id === inv._id)
            ? prev.linkedInvoiceRefs
            : [...prev.linkedInvoiceRefs, ref]
          : [ref];
      // Fill on the anchor pick only (don't clobber when adding more invoices to a discount).
      const fill = prev.type !== 'discount' || prev.linkedInvoiceRefs.length === 0;
      return {
        ...prev,
        linkedInvoiceRefs: refs,
        vesselName: fill ? inv.vesselName || prev.vesselName : prev.vesselName,
        ownerName: fill ? inv.ownerName || prev.ownerName : prev.ownerName,
        ownerAddress: fill ? inv.ownerAddress || prev.ownerAddress : prev.ownerAddress,
        agentName: fill ? inv.agentName || prev.agentName : prev.agentName,
        // D153.6: who pays always comes with the invoice (none stated = the agent).
        payer: fill ? payerOf(inv) : prev.payer,
        agentAddress: fill ? inv.agentAddress || prev.agentAddress : prev.agentAddress,
        clientName: fill ? inv.clientName || prev.clientName : prev.clientName,
        poNumber: fill ? inv.clientPoNumber || prev.poNumber : prev.poNumber,
        category: fill ? inv.category || prev.category : prev.category,
      };
    });
  };

  const removeLinkedInvoice = (invId: string) => {
    setForm((prev) => ({ ...prev, linkedInvoiceRefs: prev.linkedInvoiceRefs.filter((r) => r.id !== invId) }));
  };

  // D157.16: the print is the server PDF of the SAVED note (tax-invoice frame), like the invoice's.
  const triggerPrint = () => {
    if (isNew || !id) {
      setError('Save the credit note first, then print it.');
      return;
    }
    void openPrintInNewTab(() => fetchCreditNotePdf(id), {
      onBlocked: () => setError('Allow pop-ups for this site, then press Print again.'),
      onError: (e) => setError(errorMessageOf(e, 'The credit note could not be printed.')),
    });
  };

  const buildPayload = (): Partial<CreditNote> => {
    const base: Partial<CreditNote> = {
      type: form.type as CreditNoteType,
      category: form.category,
      vesselName: form.vesselName,
      ownerName: form.ownerName,
      ownerAddress: form.ownerAddress,
      agentName: form.agentName,
      payer: form.payer,
      agentAddress: form.agentAddress,
      clientName: form.clientName,
      poNumber: form.poNumber,
      currency: noteCurrencyOf(form),
      // D61 (USC-BUG-0027) — sent every save, blank included: blank is "no date"
      // and the server goes back to the issue timestamp for it.
      creditNoteDate: form.creditNoteDate,
      notes: form.notes,
      reasonKey: form.reasonKey,
      reasonNote: form.reasonNote,
    };
    // The CR number is issued by the server at create time — never sent from the client,
    // so two open drafts can't claim the same number.
    // Linked invoice(s) recorded on every type (the note's source invoice → CN-8 linkage).
    base.linkedInvoices = form.linkedInvoiceRefs.map((r) => r.id);
    base.linkedInvoiceNumbers = form.linkedInvoiceRefs.map((r) => r.number);
    // D44 #7 — every save of an Item Return says where its goods went, answered or not.
    if (form.type === 'item_return') base.goodsDestination = form.goodsDestination;
    const intoStock = form.type === 'item_return' && form.goodsDestination === 'united_stock';
    if (form.type === 'discount') {
      base.discountPercent = Number(form.discountPercent) || 0;
    } else {
      base.items = form.items.map((it, idx) => ({
        lineNumber: idx + 1,
        quantity: Number(it.quantity) || 0,
        uom: it.uom,
        description: it.description,
        unitPrice: Number(it.unitPrice) || 0,
        extensionPrice: computeExtension(it.quantity, it.unitPrice),
        sourceInvoiceLineNumber: it.sourceInvoiceLineNumber ?? null,
        // What is on screen, the filled-in figures included; only for goods back into stock.
        stockReturn: intoStock
          ? stockReturnPayloadOf(
              it.split,
              sourceLineOf(returnSources, it.sourceInvoiceLineNumber),
              Math.abs(Number(it.quantity) || 0)
            )
          : [],
      }));
    }
    return base;
  };

  /** What stops a save before it is sent; the server refuses the same things with codes. */
  const saveBlocker = (): string | null => {
    // D109.6: a refused typed date is not emitted, so the form still holds the previous one.
    if (!dateValid) return 'Fix the credit note date first: it is not a real day.';
    if (!form.type) return 'Choose a credit note type before saving.';
    if (!form.reasonKey) return 'Choose a reason for this credit note.';
    if (!form.linkedInvoiceRefs.length && !form.currency) return 'Pick the credit note currency (US$ or TT$).';
    const reason = reasons.find((r) => r.key === form.reasonKey);
    if (reason?.noteRequired && !form.reasonNote.trim()) return `Add a note to explain "${reason.label}".`;
    return null;
  };

  const vesselForReturn = returnSources?.vesselName || form.vesselName || 'the vessel';

  /**
   * What stops an Issue on top of a save (D44 #7, #8). The server refuses the same
   * (CREDIT_NOTE_GOODS_DESTINATION_REQUIRED, CN_RETURN_NO_BATCH); saying so first
   * spares a round trip. Returns the lines to mark with the message.
   */
  const issueBlocker = (): { message: string; lines: number[] } | null => {
    if (form.type !== 'item_return') return null;
    if (!form.goodsDestination) {
      return {
        message:
          'Say where the returned goods went before issuing: back into United stock, straight to the vendor, or not returned.',
        lines: [],
      };
    }
    if (form.goodsDestination !== 'united_stock') return null;
    if (returnSourcesLoading) {
      return { message: "Still reading where this invoice's goods went. Try again in a moment.", lines: [] };
    }
    const stuck = form.items.find(
      (it) => it.sourceInvoiceLineNumber && sourceLineOf(returnSources, it.sourceInvoiceLineNumber)?.sources.length === 0
    );
    if (stuck?.sourceInvoiceLineNumber) {
      return {
        message: `Line ${stuck.sourceInvoiceLineNumber}${stuck.description ? ` (${stuck.description})` : ''}: ${noSourcesMessage(
          vesselForReturn
        )}`,
        lines: [stuck.sourceInvoiceLineNumber],
      };
    }
    return null;
  };

  /**
   * The save already in flight, if any. Ref, not state: two clicks in the same
   * tick both read the same stale `saving === false`, so the disabled button
   * alone does not close the double-submit window — on a new note that posted
   * twice and created two credit notes, CR0039 and CR0040 (USC-BUG-0027).
   * Checked and set synchronously, so the second call joins the save already
   * running instead of starting a second one. Same pattern as
   * QuotationDetailPage's `confirmingOrderRef`.
   */
  const persistingRef = useRef<Promise<CreditNote> | null>(null);

  /** Create or update the draft; resolves to the saved note. */
  const persistDraft = async (): Promise<CreditNote> => {
    if (persistingRef.current) return persistingRef.current;
    const payload = buildPayload();
    const run = isNew ? createCreditNote(payload) : updateCreditNote(form._id as string, payload);
    persistingRef.current = run;
    try {
      return await run;
    } finally {
      persistingRef.current = null;
    }
  };

  const handleSave = async () => {
    const blocker = saveBlocker();
    if (blocker) {
      setError(blocker);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await persistDraft();
      navigate('/credit-notes');
    } catch (e: unknown) {
      setError(errorMessageOf(e, 'Failed to save credit note'));
      setFlaggedLines(refusedLineNumbers(e));
    } finally {
      setSaving(false);
    }
  };

  // D44 — Issue saves the form first, so what is issued is what is on screen,
  // never an older copy of the draft.
  const handleIssue = async () => {
    const blocker = saveBlocker();
    if (blocker) {
      setError(blocker);
      return;
    }
    const issueBlock = issueBlocker();
    if (issueBlock) {
      setError(issueBlock.message);
      setFlaggedLines(issueBlock.lines);
      return;
    }
    const ok = await confirm({
      title: 'Issue this credit note?',
      summary:
        form.type === 'item_return' && form.goodsDestination === 'united_stock'
          ? 'It becomes the client’s credit and is locked, and its goods go back into the batches chosen. To change it later, cancel it and raise a new one.'
          : 'It becomes the client’s credit and is locked. To change it later, cancel it and raise a new one.',
      confirmLabel: 'Issue',
    });
    if (!ok) return;
    setLifecycleBusy(true);
    setError(null);
    setFlaggedLines([]);
    let saved: CreditNote;
    try {
      saved = await persistDraft();
    } catch (e: unknown) {
      setError(errorMessageOf(e, 'Failed to save credit note'));
      setFlaggedLines(refusedLineNumbers(e));
      setLifecycleBusy(false);
      return;
    }
    try {
      const issued = await issueCreditNote(String(saved._id));
      // D44 #9 — an issued Item Return offers "Return to supplier?" next.
      offerAfterIssue.current = issued.type === 'item_return';
      setForm(formFromNote(issued));
      if (isNew && issued._id) navigate(`/credit-notes/${issued._id}`);
    } catch (e: unknown) {
      const message = errorMessageOf(e, 'Failed to issue credit note');
      // A stock-return refusal names the invoice line it is about (CN_RETURN_*).
      const lines = refusedLineNumbers(e);
      if (isNew && saved._id) {
        // The draft now exists. Move onto it, so trying again updates THAT draft
        // instead of creating a second one — and keep the reason Issue failed.
        carriedError.current = `Saved as a draft, but not issued: ${message}`;
        carriedFlags.current = lines;
        navigate(`/credit-notes/${saved._id}`);
      } else {
        setError(message);
        setFlaggedLines(lines);
      }
    } finally {
      setLifecycleBusy(false);
    }
  };

  const handleCancelNote = async () => {
    if (!form._id) return;
    // D44 #10, #39 — what else a cancel undoes, said before it happens.
    const putBack = form.status === 'issued' && form.type === 'item_return' && form.goodsDestination === 'united_stock';
    const draftReturns = raisedReturnsOf(supplierOffer)
      .filter((r) => r.status === 'draft')
      .map((r) => r.debitNoteNumber);
    const ok = await confirm({
      title: `Cancel credit note ${form.creditNoteNumber}?`,
      summary: [
        'It stops counting against the invoice.',
        putBack
          ? 'The goods it put back into United stock come back out where they are still free (not since shipped, reserved or held); anything that has already left is recorded on the note.'
          : '',
        draftReturns.length
          ? `Its draft vendor ${draftReturns.length === 1 ? 'return' : 'returns'} ${draftReturns.join(', ')} ${
              draftReturns.length === 1 ? 'is' : 'are'
            } cancelled with it.`
          : '',
        'A cancelled note cannot be edited or issued again.',
      ]
        .filter(Boolean)
        .join(' '),
      tone: 'error',
      confirmLabel: 'Cancel note',
      cancelLabel: 'Keep it',
    });
    if (!ok) return;
    setLifecycleBusy(true);
    setError(null);
    setFlaggedLines([]);
    try {
      const cancelled = await cancelCreditNote(form._id);
      setForm(formFromNote(cancelled));
    } catch (e: unknown) {
      // e.g. CREDIT_NOTE_HAS_ACTIVE_VENDOR_RETURN: the message names the vendor returns.
      setError(errorMessageOf(e, 'Failed to cancel credit note'));
      setFlaggedLines(refusedLineNumbers(e));
    } finally {
      setLifecycleBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full">
        <LoadingState text="Loading credit note..." />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      {/* A detail page, so the title is the note itself rather than the sidebar's
          module label, and the way back sits in the header's left group. */}
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <BackArrow label="Back to credit notes" onClick={() => navigate('/credit-notes')} />
            <h1 className="text-xl font-semibold leading-7 text-slate-900">
              {isNew ? 'New Credit Note' : `Credit Note ${form.creditNoteNumber}`}
            </h1>
            <ModuleInfoButton content={PAGE_INFO} ariaLabel="Credit note information" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={triggerPrint}
              icon={<Printer aria-hidden="true" className="h-4 w-4" />}
              title="Print the saved credit note as a PDF in the tax-invoice layout"
            >
              Print
            </Button>
            {/* D44 #9 — stays offered while the note still has a vendor return to raise. */}
            {form.status === 'issued' && form.type === 'item_return' && hasRaisableSupplierReturn(supplierOffer) && (
              <Button onClick={() => setSupplierDialogOffer(supplierOffer)} disabled={lifecycleBusy}>
                Return to supplier
              </Button>
            )}
            {!isNew && (form.status === 'draft' || form.status === 'issued') && (
              <Button
                variant="danger"
                onClick={handleCancelNote}
                loading={lifecycleBusy && !saving}
                disabled={saving || lifecycleBusy}
              >
                Cancel note
              </Button>
            )}
            {!locked && (
              <>
                <Button onClick={handleSave} loading={saving} disabled={!form.type || lifecycleBusy}>
                  Save draft
                </Button>
                <Button
                  variant="primary"
                  onClick={handleIssue}
                  loading={lifecycleBusy}
                  disabled={!form.type || saving}
                  title="Save and issue: the note becomes the client's credit and is locked"
                >
                  Issue
                </Button>
              </>
            )}
          </div>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Vessel: {form.vesselName || '--'} • Client: {form.clientName || '--'} • Status:{' '}
          <span data-testid="credit-note-status">{formatStatusLabel(form.status)}</span>
        </p>
      </div>

      {/* D161.9: bottom room (as the invoice pane) so the assistant bubble never covers the grand total. */}
      <div ref={paneRef} data-testid="credit-note-body" className="px-4 py-4 pb-24 flex-1 overflow-auto space-y-4 sm:px-8">
        {error && <ErrorState message={error} />}

        {locked && (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            {form.status === 'cancelled'
              ? 'This credit note is cancelled. It no longer counts against the invoice and cannot be changed.'
              : 'This credit note is issued and locked. To change it, cancel it and raise a new one.'}
          </div>
        )}

        {/* D44 #39 — goods that had already left their batch when the note was cancelled. */}
        {form.status === 'cancelled' && form.stockReturn && (
          <StockReturnShortfallNotice shortfall={form.stockReturn.shortfall} />
        )}

        {/* D44 — everything below is read-only once the note is issued or cancelled.
            A disabled fieldset disables every control inside it, so no field can
            be missed; the server refuses the edit either way. */}
        <fieldset disabled={locked} className="m-0 min-w-0 space-y-4 border-0 p-0">

        {/* Type selector */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-semibold text-slate-700">Credit note type</div>
          </div>
          {/* The type is frozen once the note exists. `handleTypeChange` already
              refuses the change; this wrapper is what makes the refusal visible,
              and it stays on the wrapper because SegmentedControl has no
              disabled state of its own to reach for. */}
          <div
            aria-disabled={!isNew}
            className={isNew ? '' : 'pointer-events-none opacity-60'}
          >
            <SegmentedControl
              aria-label="Credit note type"
              value={form.type}
              // A brand-new note has no type yet, and '' is not one of the
              // segments — nothing is pressed until the user picks.
              onChange={(next) => { if (next) handleTypeChange(next); }}
              segments={TYPE_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
            />
          </div>
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            Only one type per credit note —{' '}
            {isNew
              ? 'choosing a different type resets the fields below.'
              : 'the type cannot be changed after the note is created.'}
          </div>
        </div>

        {/* Header fields */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-semibold text-slate-700">Credit note header</div>
            {cur && <div className="text-xs text-slate-400">Amounts in {cur}</div>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className={labelClass}>Credit note number</label>
              <input
                className={inputClass}
                value={form.creditNoteNumber}
                readOnly
                placeholder="Auto-generated"
                title={isNew ? 'Issued when the note is saved' : undefined}
              />
            </div>
            <div>
              <label className={labelClass}>Vessel name</label>
              <input
                className={inputClass}
                value={form.vesselName}
                onChange={(e) => patch('vesselName', e.target.value)}
                placeholder="LM Ares"
              />
            </div>
            <div>
              <label className={labelClass}>Owner name</label>
              <input
                className={inputClass}
                value={form.ownerName}
                onChange={(e) => patch('ownerName', e.target.value)}
                placeholder="Vessel owner"
              />
            </div>
            <div>
              <label className={labelClass}>Agent</label>
              <input
                className={inputClass}
                value={form.agentName}
                onChange={(e) => patch('agentName', e.target.value)}
                placeholder="Local/port agent"
              />
            </div>
            <div>
              <label className={labelClass}>Payer</label>
              <select aria-label="Payer" className={inputClass} value={form.payer} onChange={(e) => patch('payer', e.target.value)}>
                <option value="owner">Owner</option>
                <option value="agent">Agent</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Client</label>
              <input
                className={inputClass}
                value={form.clientName}
                onChange={(e) => patch('clientName', e.target.value)}
                placeholder="Client name"
              />
            </div>
            <div>
              <label className={labelClass}>PO number</label>
              <input
                className={inputClass}
                value={form.poNumber}
                onChange={(e) => patch('poNumber', e.target.value)}
                placeholder="Client PO ref"
              />
            </div>
            <div>
              {/* D61 (USC-BUG-0027) — the clerk picks the day the note bears, up to
                  today. Left blank it is the day it is issued, as before. The server
                  is the guard (CREDIT_NOTE_DATE_IN_FUTURE); `max` here is only so the
                  picker does not offer a day that would be refused. Locked once the
                  note is issued, like everything else on it (D44 #5). */}
              <label className={labelClass}>Date</label>
              <DateInput
                aria-label="Date"
                className={form.status === 'draft' ? inputClass : `${inputClass} bg-slate-50`}
                value={form.creditNoteDate || (form.status === 'draft' ? '' : form.dateFallback)}
                max={todayInputValue()}
                disabled={form.status !== 'draft'}
                onChange={(v) => patch('creditNoteDate', v)}
                validity={setDateValid}
              />
              {form.status === 'draft' && !form.creditNoteDate && (
                <p className="mt-1 text-xs text-slate-500">Left blank: the day it is issued.</p>
              )}
            </div>
            <div>
              <label className={labelClass}>Category</label>
              <select
                aria-label="Category"
                className={inputClass}
                value={form.category}
                onChange={(e) => patch('category', e.target.value)}
              >
                {CATEGORY_SELECT_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              {/* D96.17 — a linked note is in its invoice's currency; only a note with none is picked. */}
              <label className={labelClass}>Currency</label>
              <select
                aria-label="Currency"
                className={`${inputClass} disabled:bg-slate-50`}
                value={noteCurrencyOf(form)}
                disabled={form.status !== 'draft' || form.linkedInvoiceRefs.length > 0}
                title={form.linkedInvoiceRefs.length > 0 ? "The invoice's currency" : undefined}
                onChange={(e) => patch('currency', e.target.value)}
              >
                {CURRENCY_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              {/* D44 — status moves only through Issue and Cancel note. The dropdown
                  this replaces offered values the server never saved. */}
              <label className={labelClass}>Status</label>
              <input className={`${inputClass} bg-slate-50`} value={formatStatusLabel(form.status)} readOnly />
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Owner address</label>
              <textarea
                className="w-full min-h-[60px] rounded-md border border-slate-200 p-2 text-xs focus:border-primary-500 resize-y"
                value={form.ownerAddress}
                onChange={(e) => patch('ownerAddress', e.target.value)}
                placeholder="Full owner address"
                rows={3}
              />
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Agent address</label>
              <textarea
                aria-label="Agent address"
                className="w-full min-h-[60px] rounded-md border border-slate-200 p-2 text-xs focus:border-primary-500 resize-y"
                value={form.agentAddress}
                onChange={(e) => patch('agentAddress', e.target.value)}
                placeholder="Full agent address"
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* D44 — why the credit is given. One reason for the whole note. */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="mb-3 text-xs font-semibold text-slate-700">Reason for the credit</div>
          <CreditReasonField
            reasons={reasons}
            reasonKey={form.reasonKey}
            reasonNote={form.reasonNote}
            savedKey={form.savedReasonKey}
            onChange={({ reasonKey, reasonNote }) => setForm((prev) => ({ ...prev, reasonKey, reasonNote }))}
            hint="Internal: not printed on the client's credit note."
          />
        </div>

        {!form.type && (
          <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
            Choose a credit note type above to continue.
          </div>
        )}

        {/* Source invoice — the note relates to a client invoice; selecting it auto-fills the
            header (vessel/owner/agent/PO#/category), still editable. Discount may link several. */}
        {form.type && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
            <div className="text-xs font-semibold text-slate-700">
              {form.type === 'discount' ? 'Linked invoices' : 'Source invoice'}
            </div>
            <InvoiceSearchSelect
              onSelect={pickInvoice}
              selectedIds={form.linkedInvoiceRefs.map((r) => r.id)}
            />

            {form.linkedInvoiceRefs.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {form.linkedInvoiceRefs.map((ref) => (
                  <span
                    key={ref.id}
                    className="inline-flex items-center gap-1.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 px-3 py-1 text-xs font-medium"
                  >
                    {ref.number || ref.id}
                    <button
                      type="button"
                      onClick={() => removeLinkedInvoice(ref.id)}
                      aria-label={`Unlink invoice ${ref.number || ref.id}`}
                      className="text-purple-400 hover:text-purple-700"
                    >
                      <X aria-hidden="true" className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-xs text-slate-500">
                Pick the invoice this credit note relates to — its vessel, owner, agent, PO # and
                category fill in automatically (still editable).
              </div>
            )}
            {form.type === 'discount' && (
              <div className="text-xs text-slate-500">
                A discount is scoped to one category and may span several invoices within it.
              </div>
            )}
          </div>
        )}

        {/* Item Return / Correction — editable line-item grid */}
        {(form.type === 'item_return' || form.type === 'correction') && (
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-semibold text-slate-700">
                Line items ({form.type === 'correction' ? 'Correction' : 'Item Return'})
              </div>
              <Button size="sm" onClick={addRow} icon={<Plus aria-hidden="true" className="h-4 w-4" />}>
                Add row
              </Button>
            </div>
            <div className="text-xs text-slate-500 mb-2">
              Enter quantity and unit price as positive numbers — the system stores the quantity and extension price as
              negative automatically.
            </div>

            {/* The invoice's own lines. A credit note against an invoice should credit
                THAT invoice's goods at the price the client was billed, and only as
                many as the invoice still has to give back — so the lines are offered
                rather than retyped, and each says how much is left. */}
            {creditableLoading && (
              <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                Loading the lines on this invoice…
              </div>
            )}
            {!creditableLoading && creditable && (
              <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                  <div className="text-xs font-semibold text-slate-700">
                    Lines on {creditable.invoiceNumber}
                  </div>
                  <div className="text-xs text-slate-500">
                    Invoice {moneyIn(creditable.invoiceTotal, creditable.currency)}
                    {creditable.creditedTotal > 0 && <> · already credited {moneyIn(creditable.creditedTotal, creditable.currency)}</>}
                  </div>
                </div>
                {creditable.lines.length === 0 ? (
                  <div className="text-xs text-slate-500">This invoice has no lines to credit.</div>
                ) : (
                  // A picker read in bulk, so `data-table-dense`. The header and
                  // cell padding come from index.css; the widths and alignments
                  // below are the genuine per-cell overrides.
                  <table className="data-table data-table-grid data-table-dense">
                    <thead>
                      <tr>
                        <th className="w-10">#</th>
                        <th>Item</th>
                        <th className="w-16">UOM</th>
                        <th className="w-20 text-right">Billed</th>
                        <th className="w-24 text-right">Unit price</th>
                        <th className="w-28 text-right">Left to credit</th>
                        <th className="w-20" />
                      </tr>
                    </thead>
                    <tbody>
                      {creditable.lines.map((line) => {
                        const left = remainingOnLine(line);
                        return (
                          <tr key={line.lineNumber}>
                            <td className="text-slate-500">{line.lineNumber}</td>
                            <td>{line.itemName || line.itemDescription || `Line ${line.lineNumber}`}</td>
                            <td className="text-slate-500">{line.uom || '--'}</td>
                            <td className="text-right tabular-nums">{line.quantity}</td>
                            <td className="text-right tabular-nums">
                              {line.unitPrice === null ? '--' : moneyIn(line.unitPrice, creditable.currency)}
                            </td>
                            <td className={`text-right font-semibold tabular-nums ${left > 0 ? 'text-slate-700' : 'text-slate-400'}`}>
                              {left} of {line.quantity}
                            </td>
                            <td className="text-right">
                              <Button
                                size="sm"
                                disabled={left <= 0}
                                onClick={() => addInvoiceLine(line)}
                                title={left <= 0 ? 'Fully credited already' : 'Add this line to the credit note'}
                              >
                                {left <= 0 ? 'Credited' : 'Add'}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            <div className="overflow-auto">
              {/* `data-table-dense` because this is an editable grid scanned in
                  bulk; the widths, alignments and `align-top` below are the
                  genuine per-cell overrides and still win over index.css. */}
              <table className="data-table data-table-grid data-table-dense">
                <thead>
                  <tr>
                    <th className="w-24">Quantity</th>
                    <th className="w-28">UOM</th>
                    <th>Description</th>
                    <th className="w-32 text-right">Unit price {cur}</th>
                    <th className="w-36 text-right">Ext. price {cur} (neg.)</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {form.items.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-6 text-center text-slate-400">
                        {creditable
                          ? 'No line items yet — add them from the invoice lines above.'
                          : 'No line items. Click Add row.'}
                      </td>
                    </tr>
                  )}
                  {form.items.map((item, idx) => {
                    const ext = computeExtension(item.quantity, item.unitPrice);
                    const cap = capForRow(item, idx);
                    // An item return goes back at the price it was sold at. A
                    // correction is a re-pricing by definition, so it stays editable
                    // (the server caps its money against the line either way).
                    const priceLocked = form.type === 'item_return' && Boolean(item.sourceInvoiceLineNumber);
                    const flagged = Boolean(
                      item.sourceInvoiceLineNumber && flaggedLines.includes(item.sourceInvoiceLineNumber)
                    );
                    return (
                      // Every cell is align-top so the inputs share one baseline. A
                      // <td> defaults to vertical-align:middle, so the moment the
                      // quantity and description cells grew a hint line under them
                      // the row got taller and the cells WITHOUT a hint — UOM, unit
                      // price, the extension, the remove button — re-centred
                      // themselves half a line lower than the ones with. The two
                      // text-only cells then need an h-8 line box of their own to sit
                      // on the inputs' text rather than on their top edge.
                      <tr
                        key={idx}
                        data-flagged={flagged ? 'true' : undefined}
                        className={flagged ? 'bg-red-50' : undefined}
                      >
                        <td className="align-top">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            max={cap ?? undefined}
                            className="w-full h-8 rounded-md border border-slate-200 px-2 text-xs"
                            value={displayNumber(idx, 'quantity', item.quantity)}
                            onChange={(e) => updateItemNumber(idx, 'quantity', e.target.value)}
                            onBlur={() => commitNumber(idx, 'quantity')}
                          />
                          {cap !== null && (
                            <div className="mt-0.5 text-xs text-slate-400">max {cap}</div>
                          )}
                        </td>
                        <td className="align-top">
                          <input
                            className="w-full h-8 rounded-md border border-slate-200 px-2 text-xs"
                            value={item.uom}
                            onChange={(e) => updateItem(idx, { uom: e.target.value })}
                            placeholder="pck / kg / cs"
                          />
                        </td>
                        <td className="align-top">
                          <input
                            className="w-full h-8 rounded-md border border-slate-200 px-2 text-xs"
                            value={item.description}
                            onChange={(e) => updateItem(idx, { description: e.target.value })}
                            placeholder="Description"
                          />
                          {item.sourceInvoiceLineNumber ? (
                            <div className="mt-0.5 text-xs text-slate-400">
                              from invoice line {item.sourceInvoiceLineNumber}
                            </div>
                          ) : (
                            creditable && (
                              <div className="mt-0.5 text-xs text-amber-600">
                                not linked to an invoice line — add it from the list above to cap it
                              </div>
                            )
                          )}
                        </td>
                        <td className="align-top">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            readOnly={priceLocked}
                            title={priceLocked ? 'Returned at the price the client was billed' : undefined}
                            className={`w-full h-8 rounded-md border border-slate-200 px-2 text-xs text-right ${
                              priceLocked ? 'bg-slate-50 text-slate-500' : ''
                            }`}
                            value={displayNumber(idx, 'unitPrice', item.unitPrice)}
                            onChange={(e) => updateItemNumber(idx, 'unitPrice', e.target.value)}
                            onBlur={() => commitNumber(idx, 'unitPrice')}
                          />
                        </td>
                        <td className="align-top">
                          <div className="h-8 flex items-center justify-end font-semibold tabular-nums text-red-700">
                            {money(ext)}
                          </div>
                        </td>
                        <td className="align-top">
                          <div className="h-8 flex items-center justify-center">
                            <button
                              type="button"
                              onClick={() => removeItem(idx)}
                              className="text-slate-400 hover:text-red-600"
                              aria-label={`Remove row ${idx + 1}`}
                              title="Remove row"
                            >
                              <X aria-hidden="true" className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* D44 #7, #8 (VR-4) — Item Return only: where the goods went, asked once for
            the note, and for goods back into United stock, which batches take them. */}
        {form.type === 'item_return' && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
            <div className="text-xs font-semibold text-slate-700">Where the returned goods went</div>
            <GoodsDestinationField
              value={form.goodsDestination}
              onChange={(next) => patch('goodsDestination', next)}
              missing={!locked && !form.goodsDestination}
            />
            {form.goodsDestination === 'united_stock' &&
              (locked ? (
                <StockReturnSummary
                  status={form.status}
                  rows={form.items.map((it) => ({
                    lineNumber: it.sourceInvoiceLineNumber ?? null,
                    description: it.description,
                    entries: it.stockReturn || [],
                  }))}
                  record={form.stockReturn}
                  sources={returnSources}
                />
              ) : (
                <StockReturnSplit
                  rows={form.items.map((it, index) => ({
                    index,
                    lineNumber: it.sourceInvoiceLineNumber ?? null,
                    description: it.description,
                    uom: it.uom,
                    credited: Math.abs(Number(it.quantity) || 0),
                    split: it.split,
                  }))}
                  sources={returnSources}
                  loading={returnSourcesLoading}
                  error={returnSourcesError}
                  hasInvoice={Boolean(anchorInvoiceId)}
                  vesselName={vesselForReturn}
                  flaggedLines={flaggedLines}
                  onChange={updateSplit}
                />
              ))}
            {supplierOfferError && <ErrorState message={supplierOfferError} />}
          </div>
        )}

        {/* Discount — linked invoices + % + live net value preview */}
        {form.type === 'discount' && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
            <div className="text-xs font-semibold text-slate-700">Discount</div>
            {form.linkedInvoiceRefs.length === 0 && (
              <div className="text-xs text-slate-500">
                Select at least one invoice above to calculate a discount.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>Discount %</label>
                {/* Same raw-while-typing treatment as the line grid: reformatting on
                    every keystroke made "12.5" impossible to type here too. */}
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  className={inputClass}
                  value={rawPercent !== undefined ? rawPercent : formatEditableNumber(form.discountPercent)}
                  onChange={(e) => {
                    setRawPercent(e.target.value);
                    const n = e.target.value === '' ? 0 : Number(e.target.value);
                    patch('discountPercent', Number.isFinite(n) ? round2(n) : 0);
                  }}
                  onBlur={() => setRawPercent(undefined)}
                />
              </div>
              <div>
                <label className={labelClass}>Net invoice value</label>
                <div className="h-8 rounded-md border border-slate-200 bg-slate-50 px-2 text-xs flex items-center font-semibold tabular-nums text-slate-700">
                  {money(netInvoiceValue)}
                </div>
              </div>
              <div>
                <label className={labelClass}>Discount amount (negative)</label>
                <div className="h-8 rounded-md border border-slate-200 bg-slate-50 px-2 text-xs flex items-center font-semibold tabular-nums text-red-700">
                  {money(grandTotal)}
                </div>
              </div>
            </div>

            {previewError && (
              <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
                {previewError}
              </div>
            )}

            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              The percentage is applied to the <strong>net</strong> value — the linked invoice totals minus any prior
              credit notes already raised against them, never the original invoice amount.
            </div>

            <div>
              <label className={labelClass}>Description (auto — prints on the note)</label>
              <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-600 whitespace-pre-wrap min-h-[2.5em]">
                {discountDescription || 'Select an invoice and enter a percentage.'}
              </div>
            </div>
          </div>
        )}

        {/* Grand total */}
        {form.type && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-between">
            <div className="text-xs font-semibold text-slate-700">Grand total {cur} (negative)</div>
            <div className="text-xl font-bold tabular-nums text-red-700">{money(grandTotal)}</div>
          </div>
        )}

        {/* Notes */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <label className={labelClass}>Notes</label>
          <textarea
            className="w-full min-h-[70px] rounded-md border border-slate-200 p-2 text-xs focus:border-primary-500 resize-y"
            value={form.notes}
            onChange={(e) => patch('notes', e.target.value)}
            placeholder="Internal notes (optional)"
          />
        </div>
        </fieldset>
      </div>

      {supplierDialogOffer && form._id && (
        <SupplierReturnDialog
          creditNoteId={form._id}
          creditNoteNumber={form.creditNoteNumber}
          offer={supplierDialogOffer}
          onClose={() => setSupplierDialogOffer(null)}
          onRaised={() => setSupplierOfferReload((n) => n + 1)}
        />
      )}
    </div>
  );
};

export default CreditNoteDetailPage;
